<template>
  <cargo-ag-grid
    v-if="showFirst"
    key="1"
    ref="grid"
    :grid-options="gridOptions"
    :column-defs="finalColumnDefs"
    :config-module="configModule"
    :pre-column-state-callback="preColumnStateCallbackWrap"
    :custom-menu-items="customMenuItems"
    :row-data="rowData"
    v-bind="$attrs"
    :use-cache-init.sync="useCacheInit"
    :show-menu-items="showMenuItems"
    v-on="$listeners"
    @gridReady="onGridReady"
    @columnRowGroupChanged="onColumnRowGroupChanged"
  />
  <cargo-ag-grid
    v-else
    key="2"
    ref="grid"
    :grid-options="gridOptions"
    :column-defs="finalColumnDefs"
    :config-module="configModule"
    :pre-column-state-callback="preColumnStateCallbackWrap"
    :custom-menu-items="customMenuItems"
    :row-data="rowData"
    v-bind="$attrs"
    :use-cache-init.sync="useCacheInit"
    :show-menu-items="showMenuItems"
    v-on="$listeners"
    @gridReady="onGridReady"
    @columnRowGroupChanged="onColumnRowGroupChanged"
  />
</template>

<script>
import { formatNumberToThousands } from '@/utils'

export default {
  name: 'CargoAgGridWrapper',
  props: {
    gridOptions: {
      type: Object,
      required: true
    },
    columnDefs: {
      type: Array,
      required: true
    },
    sumColumnDefs: { // 合计列定义
      type: Array,
      default: null
    },
    showRowGroups: {
      type: Boolean,
      default: false
    },
    configModule: {
      type: String,
      default: ''
    },
    dargGroupShowColumnDef: {
      type: Object,
      default: null
    },
    preColumnStateCallback: { // 列状态预处理回调方法
      type: Function,
      default: null
    },
    rowData: {
      type: Array,
      default: () => {
        return []
      }
    },
    customsColumnDefs: {
      type: Array,
      default: () => {
        return []
      }
    },
    showMenuItems: { // 是否显示菜单项
      type: Boolean,
      default: true
    },
    rowGroupDefaultCollapse: { // 数据分组是否默认收起
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showFirst: true,
      treeData: false,
      groupDefaultExpanded: 0,
      autoGroupColumnDef: null,
      groupAutoGroupColumnDef: {
        cellRendererParams: {
          innerRenderer: (params) => {
            // 获取当前分组字段的名称
            const groupField = params.node?.rowGroupColumn?.colId // 获取分组列的 ID（字段名）

            // 如果当前有分组字段
            if (groupField) {
              const data = params.node.allLeafChildren[0]?.data

              // 查找当前字段对应的列定义
              const groupColumnDef = this.finalColumnDefs.find(col => col.colId === groupField || col.field === groupField)

              // 特殊处理结算对象会被转为字符串，会导致列内的解析找不到对应的合伙者伙伴
              if (groupField === 'accountId') {
                params.node.key = Number(params.node.key)
              }

              if (groupColumnDef) {
                const value = this.getCellValue({ value: params.node.key, data }, groupColumnDef)
                if (value) {
                  return value
                }
              }
            }

            // 如果没有找到分组字段，返回分组原始值
            return params.node.key
          }
        }
      },
      hasRowGroup: false, // 是否有行分组
      // 统计汇总方式的配置
      customMenuItems: {
        summaryModes: {
          key: 'summaryMode',
          configContent: {},
          setMenus: (params) => {
            if (!this.hasRowGroup) return []

            const colId = params.column.getColId()
            const summaryModes = this.customMenuItems.summaryModes.configContent[colId]

            // 判断图标是否显示的辅助函数
            const getIcon = (condition) => condition ? '<span class="ag-icon ag-icon-tick"></span>' : null
            return [
              {
                name: this.$t('grid.summaryMode'),
                subMenu: [
                  {
                    name: this.$t('grid.count'),
                    icon: getIcon(summaryModes === 'subtotal'),
                    action: () => {
                      this.updateSummaryMode(colId, 'subtotal')
                    }
                  },
                  {
                    name: this.$t('grid.groupedSubtotal'),
                    icon: getIcon(summaryModes === 'groupedSubtotal'),
                    action: () => {
                      this.updateSummaryMode(colId, 'groupedSubtotal')
                    }
                  },
                  {
                    name: this.$t('grid.sum'),
                    icon: getIcon(summaryModes === 'sum'),
                    action: () => {
                      this.updateSummaryMode(colId, 'sum')
                    }
                  },
                  {
                    name: this.$t('Analysis.CancelCounting'),
                    icon: getIcon(summaryModes === 'cancelCounting'),
                    action: () => {
                      this.updateSummaryMode(colId, 'cancelCounting')
                    }
                  }
                ]
              }
            ]
          }
        }
      },
      // currentColumnState: null, // 当前列状态
      currentSummaryModeContent: null,
      useCacheInit: false, // 是否使用缓存初始化
      cellDisplayCache: new WeakMap() // 单元格展示值缓存，减少滚动时重复计算
    }
  },
  computed: {
    sumFields() {
      if (this.sumColumnDefs) {
        const fields = Array.from(this.columnDefs, p => p.field)
        const result = []
        for (const sumColumnDef of this.sumColumnDefs) {
          if (fields.includes(sumColumnDef.field)) {
            result.push(sumColumnDef.field)
          }
        }
        return result
      }
      return []
    },
    finalColumnDefs() {
      const columnDefs = this.hasRowGroup && this.dargGroupShowColumnDef ? [...this.columnDefs, this.dargGroupShowColumnDef] : this.columnDefs
      return Array.from(columnDefs, item => {
        let checkboxSelection
        if (item.hasOwnProperty('checkboxSelection')) {
          if (typeof item.checkboxSelection === 'function') {
            checkboxSelection = params => params.data && item.checkboxSelection(params)
          } else {
            checkboxSelection = params => params.data && item.checkboxSelection
          }
        }
        let valueGetter
        if (item.valueGetter) {
          valueGetter = this.cellRendererInterceptor(params => {
            if (!params.data) {
              return ''
            }
            return this.getCachedDisplayValue('valueGetter', item, params, () => item.valueGetter(params))
          })
        }
        let valueFormatter
        if (item.valueFormatter) {
          valueFormatter = this.cellRendererInterceptor(params => {
            if (!params.data) {
              if (params.value === undefined) {
                return ''
              } else if (this.sumFields.includes(item.field)) {
                return params.value
              }
            }
            return this.getCachedDisplayValue('valueFormatter', item, params, () => item.valueFormatter(params))
          })
        }
        let cellRenderer
        if (item.cellRenderer) {
          cellRenderer = this.cellRendererInterceptor(params => {
            if (!params.data) {
              if (params.value === undefined) {
                return ''
              } else if (this.sumFields.includes(item.field)) {
                return params.value
              }
            }
            return this.getCachedDisplayValue('cellRenderer', item, params, () => item.cellRenderer(params))
          })
        }
        let filterValueGetter
        if (item.filterValueGetter) {
          filterValueGetter = params => {
            if (!params.data && params.value === undefined) {
              return ''
            }
            return item.filterValueGetter(params)
          }
        }
        let editable
        if (item.hasOwnProperty('editable')) {
          if (typeof item.editable === 'function') {
            editable = params => params.data && item.editable(params)
          } else {
            editable = params => params.data && item.editable
          }
        }
        if (checkboxSelection || valueGetter || valueFormatter || cellRenderer || filterValueGetter || editable) {
          const newItem = { ...item }
          if (checkboxSelection) {
            newItem.checkboxSelection = checkboxSelection
          }
          if (valueGetter) {
            newItem.valueGetter = valueGetter
          }
          if (valueFormatter) {
            newItem.valueFormatter = valueFormatter
          }
          if (cellRenderer) {
            newItem.cellRenderer = cellRenderer
          }
          if (filterValueGetter) {
            newItem.filterValueGetter = filterValueGetter
          }
          if (editable) {
            newItem.editable = editable
          }
          return newItem
        }
        return item
      })
    },
    preColumnStateCallbackWrap() {
      if (this.preColumnStateCallback) {
        return p => this.preColumnStateCallback(this.handelGroupColumnOrder(p))
      }
      return p => this.handelGroupColumnOrder(p)
    }
  },
  watch: {
    showRowGroups() {
      this.resetCellDisplayCache()
      this.resetRowGroups()
    },
    configModule() {
      this.resetCellDisplayCache()
      this.showFirst = !this.showFirst
    },
    rowData() {
      this.resetCellDisplayCache()
    },
    columnDefs() {
      this.resetCellDisplayCache()
    }
  },
  created() {
    this.treeData = this.gridOptions.treeData || false
    this.groupDefaultExpanded = this.gridOptions.groupDefaultExpanded || 0
    if (this.gridOptions.autoGroupColumnDef) {
      this.autoGroupColumnDef = { ...this.gridOptions.autoGroupColumnDef }
    } else {
      this.gridOptions.autoGroupColumnDef = this.groupAutoGroupColumnDef
    }
    this.resetRowGroups()
  },
  mounted() {
    window.addEventListener('beforeunload', this.saveState)
  },
  beforeDestroy() {
    this.saveState()
    window.removeEventListener('beforeunload', this.saveState)
  },
  methods: {
    onGridReady(params) {
      // if (this.currentColumnState) {
      //   params.columnApi.setColumnState(this.currentColumnState)
      //   this.currentColumnState = null
      // }
      if (this.currentSummaryModeContent) {
        this.customMenuItems.summaryModes.configContent = this.currentSummaryModeContent
        this.currentSummaryModeContent = null
      }
    },
    resetRowGroups() {
      if (this.showRowGroups) {
        // this.gridOptions.suppressDragLeaveHidesColumns = true
        // this.gridOptions.suppressMakeColumnVisibleAfterUnGroup = true
        this.gridOptions.rowGroupPanelShow = 'always'
        this.gridOptions.groupRowAggNodes = nodes => {
          if (!Array.isArray(nodes)) {
            return null
          }
          // 不是分组情况不计算汇总
          if (!this.hasRowGroup) {
            return null
          }
          // 提取有效的子节点数据
          const rows = nodes.filter(node => node.data).map(node => node.data)
          if (rows.length === 0 || !this.hasRowGroup) {
            return null
          }
          const sumResult = {}

          // 缓存节点
          const nodeMap = new Map()
          nodes.forEach((node) => {
            if (node.data) {
              nodeMap.set(node.data, node)
            }
          })

          // 预计算每列的汇总数据
          const precomputedSums = this.finalColumnDefs.reduce((acc, colDef) => {
            const { field } = colDef
            const colId = colDef.colId || colDef.field
            const configType = this.$refs.grid?.customMenuItems.summaryModes.configContent[colId]

            if (configType === 'sum' || configType === 'subtotal' || configType === 'groupedSubtotal' || configType === 'cancelCounting') {
              const values = rows.map(row => {
                const node = nodeMap.get(row)
                const params = { data: row, value: row[field], node }
                return this.getCellValue(params, colDef)
                // return row[colId]
              })
              acc[colId] = { values, configType }
            }
            return acc
          }, {})

          // 对每列的汇总计算（避免多次计算）
          Object.keys(precomputedSums).forEach(colId => {
            const { values, configType } = precomputedSums[colId]
            if (configType === 'sum') {
              // 汇总
              const sumValue = values.reduce((sum, value) => {
                const numericValue = typeof value === 'number' ? value : Number(value.replace(/,/g, ''))
                return !isNaN(numericValue) ? sum + numericValue : sum
              }, 0)
              sumResult[colId] = sumValue ? formatNumberToThousands(sumValue, 2) : 0
            } else if (configType === 'subtotal') {
              // 小计
              const count = values.filter(value => value !== undefined && value !== null && value !== '').length
              sumResult[colId] = `${this.$t('grid.count')}: ${count}`
            } else if (configType === 'groupedSubtotal') {
              // 分项小计
              const groupedResult = values.reduce((result, value) => {
                const key = value || this.$t('SystemMaintenance.Empty')
                result[key] = (result[key] || 0) + 1
                return result
              }, {})
              sumResult[colId] = Object.entries(groupedResult)
                .map(([key, count]) => `${key}: ${count}`)
                .join(', ')
            } else if (configType === 'cancelCounting') {
              sumResult[colId] = values.length > 0 ? values[0] : ''
            }
          })

          // 处理 customsColumnDefs 自定义字段（如果有的话）
          if (this.customsColumnDefs && this.customsColumnDefs.length > 0) {
            this.customsColumnDefs.forEach(columnDef => {
              const { field } = columnDef
              sumResult[field] = columnDef.getContent(rows)
            })
          }
          return sumResult
        }

        this.gridOptions.groupDefaultExpanded = this.rowGroupDefaultCollapse ? 0 : -1
      } else {
        // delete this.gridOptions.suppressDragLeaveHidesColumns
        // delete this.gridOptions.suppressMakeColumnVisibleAfterUnGroup
        delete this.gridOptions.rowGroupPanelShow
        delete this.gridOptions.groupRowAggNodes
        this.gridOptions.groupDefaultExpanded = this.groupDefaultExpanded
      }
      this.showFirst = !this.showFirst
    },
    setListSort(params, oldSort) {
      return this.$refs.grid.setListSort(params, oldSort)
    },
    resetSort2(params) {
      return this.$refs.grid.resetSort2(params)
    },
    onColumnRowGroupChanged(event) {
      const columns = event.columns
      if (columns && columns.length > 0) {
        this.hasRowGroup = true
        if (this.gridOptions.treeData) {
          if (this.gridOptions.columnApi) {
          //   this.currentColumnState = this.gridOptions.columnApi.getColumnState()
            this.currentSummaryModeContent = this.customMenuItems.summaryModes.configContent
          }
          this.gridOptions.treeData = false
          this.gridOptions.autoGroupColumnDef = this.groupAutoGroupColumnDef
          this.useCacheInit = true
          this.resetRowGroups()
        }
      } else {
        this.hasRowGroup = false
        if (this.treeData) {
          if (this.gridOptions.columnApi) {
            this.currentSummaryModeContent = this.customMenuItems.summaryModes.configContent
          }
          this.gridOptions.treeData = true
          this.gridOptions.autoGroupColumnDef = this.autoGroupColumnDef
          this.useCacheInit = true
          this.resetRowGroups()
        }
      }
      this.$emit('columnRowGroupChanged', event)
    },
    /**
     * 单元格渲染拦截器，用于处理分组行和普通行的渲染逻辑。
     * - 分组行：直接返回分组键值。
     * - 普通行：调用原始渲染逻辑。
     *
     * @param {Function} originalRenderer - 原始渲染逻辑函数。
     * @returns {Function} 包装后的渲染函数。
     */
    cellRendererInterceptor(originalRenderer) {
      return function(params) {
        // 分组行直接返回分组键值
        if (params.node && params.node.group) {
          return params.value
        }

        // 普通行调用原始渲染逻辑
        if (typeof originalRenderer === 'function') {
          return originalRenderer(params)
        }

        return null
      }
    },
    resetCellDisplayCache() {
      this.cellDisplayCache = new WeakMap()
    },
    isCacheableValue(value) {
      return value === null || value === undefined || ['string', 'number', 'boolean'].includes(typeof value)
    },
    getCachedDisplayValue(type, columnDef, params, renderFn) {
      if (!params || !params.data || typeof renderFn !== 'function') {
        return renderFn ? renderFn(params) : null
      }
      if (params.node && params.node.group) {
        return renderFn(params)
      }
      const colId = columnDef.colId || columnDef.field
      if (!colId || !this.isCacheableValue(params.value)) {
        return renderFn(params)
      }
      let rowCache = this.cellDisplayCache.get(params.data)
      if (!rowCache) {
        rowCache = new Map()
        this.cellDisplayCache.set(params.data, rowCache)
      }
      const cacheKey = `${type}:${colId}:${typeof params.value}:${String(params.value)}`
      if (rowCache.has(cacheKey)) {
        return rowCache.get(cacheKey)
      }
      const result = renderFn(params)
      if (this.isCacheableValue(result)) {
        rowCache.set(cacheKey, result)
      }
      return result
    },

    /**
     * 获取单元格值，根据优先级顺序：cellRenderer > valueFormatter > valueGetter 字段值
     * @param {Object} params - AG-Grid 提供的参数，包含行数据和列定义
     * @param {Object} colDef - 列定义，包含 cellRenderer, valueGetter, valueFormatter 等属性
     * @returns {any} - 根据优先级计算出的值
     */
    getCellValue(params, colDef) {
      const value = (
          colDef.cellRenderer?.(params) ??
          colDef.valueFormatter?.(params) ??
          colDef.valueGetter?.(params) ??
          params.value
      )
      if (value === undefined) {
        return ''
      }
      return value
    },

    /**
     * 更新汇总模式
     * @param colId
     * @param mode
     */
    updateSummaryMode(colId, mode) {
      const currentMode = this.customMenuItems.summaryModes.configContent[colId]

      // 只有当当前模式不等于传入的模式时，才更新
      if (currentMode !== mode) {
        this.customMenuItems.summaryModes.configContent[colId] = mode
        this.gridOptions.api.setRowData(this.rowData)
        this.$refs.grid.saveState()
      }
    },
    handelGroupColumnOrder(columnState) {
      if (this.hasRowGroup) {
        if (this.dargGroupShowColumnDef) {
          const index = columnState.findIndex(p => p.colId === 'jobNo')
          if (index === -1) {
            columnState.splice(1, 0, {
              aggFunc: null,
              colId: 'jobNo',
              flex: null,
              hide: false,
              pinned: null,
              pivot: false,
              pivotIndex: null,
              rowGroup: false,
              rowGroupIndex: null,
              sort: null,
              sortIndex: null,
              width: this.dargGroupShowColumnDef.width
            })
          }
        }
      } else {
        if (this.autoGroupColumnDef) {
          const index = columnState.findIndex(p => p.colId === 'ag-Grid-AutoColumn')
          if (index === -1) {
            columnState.splice(0, 0, {
              aggFunc: null,
              colId: 'ag-Grid-AutoColumn',
              flex: null,
              hide: false,
              pinned: null,
              pivot: false,
              pivotIndex: null,
              rowGroup: false,
              rowGroupIndex: null,
              sort: null,
              sortIndex: null,
              width: this.autoGroupColumnDef.width
            })
          }
        }
      }
      return columnState
    },
    saveState() {
      if (this.$refs.grid) {
        this.$refs.grid.saveState()
      }
    }
  }
}
</script>
